// Jun-2026 — BOQ Vendor PO (no PR): raise a Draft ops PO mirroring the
// latest Approved BOQ with one uniform deduction % on every client rate.
// Lines default UNSELECTED; quantities editable but capped at BOQ qty.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SearchableSelect } from "@/components/SearchableSelect";
import { X, FileText, Percent } from "lucide-react";

const inp = "border border-slate-300 rounded px-2 py-1.5 text-xs";
const today = () => new Date().toLocaleDateString("en-CA");
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export default function BoqVendorPoModal({ project, onClose, onCreated }) {
  const [ctx, setCtx] = useState(null);
  const [vendorId, setVendorId] = useState("");
  const [pct, setPct] = useState("");
  const [gstEnabled, setGstEnabled] = useState(true);
  const [poDate, setPoDate] = useState(today());
  const [remarks, setRemarks] = useState("");
  const [sel, setSel] = useState({});          // boq_line_id -> qty (selected)
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/projects/${project.id}/boq-vendor-po/context`)
      .then((r) => setCtx(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Failed to load BOQ context"); onClose(); });
  }, [project.id, onClose]);

  const p = parseFloat(pct);
  const pctOk = !Number.isNaN(p) && p >= 0 && p < 100;
  const lines = ctx?.lines || [];
  const rateOf = (l) => (pctOk ? Math.round(Number(l.client_rate || 0) * (100 - p)) / 100 : Number(l.client_rate || 0));

  const toggle = (l) => setSel((s) => {
    const n = { ...s };
    if (n[l.id] !== undefined) delete n[l.id]; else n[l.id] = l.quantity;
    return n;
  });
  const allSelected = lines.length > 0 && lines.every((l) => sel[l.id] !== undefined);
  const toggleAll = () => setSel(allSelected ? {} : Object.fromEntries(lines.map((l) => [l.id, l.quantity])));
  const setQty = (l, v) => {
    let q = parseFloat(v);
    if (Number.isNaN(q)) q = 0;
    if (q > l.quantity) q = l.quantity;
    setSel((s) => ({ ...s, [l.id]: q }));
  };

  const totals = useMemo(() => {
    let sub = 0, gst = 0, n = 0;
    for (const l of lines) {
      const q = sel[l.id];
      if (q === undefined || q <= 0) continue;
      n += 1;
      const amt = q * rateOf(l);
      sub += amt;
      if (gstEnabled) gst += amt * Number(l.tax_percent || 0) / 100;
    }
    return { n, sub, gst, total: sub + gst };
    // eslint-disable-next-line
  }, [sel, lines, p, gstEnabled]);

  const create = async () => {
    if (!vendorId) return toast.error("Pick a vendor");
    if (!pctOk) return toast.error("Enter a deduction percentage between 0 and 99.99");
    const chosen = lines.filter((l) => sel[l.id] !== undefined && sel[l.id] > 0);
    if (!chosen.length) return toast.error("Select at least one BOQ line");
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/boq-vendor-po`, {
        vendor_id: vendorId, deduction_percent: p, gst_enabled: gstEnabled,
        po_date: poDate, remarks,
        lines: chosen.map((l) => ({ boq_line_id: l.id, quantity: sel[l.id] })),
      });
      toast.success(r.data.message || `Draft PO ${r.data.po_number} created`);
      onCreated?.(r.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "PO creation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-8" data-testid="boqpo-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 text-xs">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white rounded-t-lg z-10">
          <div>
            <div className="text-sm font-bold text-slate-900 inline-flex items-center gap-2">
              <FileText size={15} className="text-violet-600" /> BOQ Vendor PO (No PR) — {project.project_name}
            </div>
            <div className="text-[10px] text-slate-500">
              {ctx?.boq ? `Mirrors Approved BOQ ${ctx.boq.boq_number} (V${ctx.boq.version_number})` : "Loading latest approved BOQ…"} ·
              service items — no GRN · flows the PO Approval Matrix · appears in Purchase → Purchase Orders
            </div>
          </div>
          <button data-testid="boqpo-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>

        {!ctx ? (
          <div className="p-10 text-center text-slate-400">Loading…</div>
        ) : !ctx.has_boq ? (
          <div className="p-8 text-center text-amber-800 bg-amber-50 m-5 border border-amber-300 rounded" data-testid="boqpo-no-boq">
            This project has no Approved BOQ version yet — approve a BOQ first.
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <label className="space-y-1 col-span-2">
                <span className="font-bold text-slate-600">Vendor *</span>
                <SearchableSelect value={vendorId} onChange={setVendorId}
                  items={ctx.vendors || []}
                  getLabel={(v) => `${v.vendor_code ? `${v.vendor_code} · ` : ""}${v.name}`}
                  getSearchText={(v) => `${v.name} ${v.vendor_code}`}
                  placeholder="— select approved vendor —" searchPlaceholder="Search vendors…"
                  testid="boqpo-vendor-select" />
              </label>
              <label className="space-y-1">
                <span className="font-bold text-slate-600 inline-flex items-center gap-1"><Percent size={11} /> Deduction on client rates *</span>
                <input data-testid="boqpo-pct-input" type="number" min="0" max="99.99" step="0.01"
                  className={`${inp} w-full ${pct !== "" && !pctOk ? "border-red-400" : ""}`}
                  placeholder="e.g. 10" value={pct} onChange={(e) => setPct(e.target.value)} />
              </label>
              <label className="space-y-1">
                <span className="font-bold text-slate-600">PO Date</span>
                <input data-testid="boqpo-date-input" type="date" className={`${inp} w-full`}
                  value={poDate} onChange={(e) => setPoDate(e.target.value)} />
              </label>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 font-bold text-slate-700 cursor-pointer">
                <input type="checkbox" data-testid="boqpo-gst-toggle" checked={gstEnabled}
                  onChange={(e) => setGstEnabled(e.target.checked)} className="w-4 h-4" />
                Apply GST (per BOQ line tax %)
              </label>
              <input data-testid="boqpo-remarks" className={`${inp} flex-1 min-w-[240px]`} maxLength={500}
                placeholder="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </div>

            <div className="border border-slate-200 rounded overflow-x-auto max-h-[46vh] overflow-y-auto">
              <table className="w-full min-w-[900px] text-xs" data-testid="boqpo-lines-table">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5"><input type="checkbox" data-testid="boqpo-select-all" checked={allSelected} onChange={toggleAll} /></th>
                    <th className="px-2 py-1.5 text-left">Code</th>
                    <th className="px-2 py-1.5 text-left">Item (from BOQ)</th>
                    <th className="px-2 py-1.5 text-left">UOM</th>
                    <th className="px-2 py-1.5 text-right">BOQ Qty</th>
                    <th className="px-2 py-1.5 text-right">PO Qty</th>
                    <th className="px-2 py-1.5 text-right">Client Rate</th>
                    <th className="px-2 py-1.5 text-right">PO Rate (−{pctOk ? p : 0}%)</th>
                    <th className="px-2 py-1.5 text-right">GST %</th>
                    <th className="px-2 py-1.5 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const on = sel[l.id] !== undefined;
                    const q = on ? sel[l.id] : l.quantity;
                    const r = rateOf(l);
                    return (
                      <tr key={l.id} className={`border-t border-slate-100 ${on ? "bg-violet-50/50" : ""}`} data-testid={`boqpo-line-${i}`}>
                        <td className="px-2 py-1 text-center">
                          <input type="checkbox" data-testid={`boqpo-line-check-${i}`} checked={on} onChange={() => toggle(l)} />
                        </td>
                        <td className="px-2 py-1 font-mono font-bold">{l.element_code}</td>
                        <td className="px-2 py-1 max-w-[240px] truncate" title={l.name}>{l.name}</td>
                        <td className="px-2 py-1">{l.uom_name}</td>
                        <td className="px-2 py-1 text-right">{l.quantity}</td>
                        <td className="px-2 py-1 text-right">
                          <input data-testid={`boqpo-line-qty-${i}`} type="number" min="0.01" max={l.quantity} step="any"
                            className={`${inp} w-24 text-right py-0.5`} disabled={!on}
                            value={q} onChange={(e) => setQty(l, e.target.value)} />
                        </td>
                        <td className="px-2 py-1 text-right">{inr(l.client_rate)}</td>
                        <td className="px-2 py-1 text-right font-bold text-violet-800" data-testid={`boqpo-line-rate-${i}`}>{inr(r)}</td>
                        <td className="px-2 py-1 text-right">{gstEnabled ? `${l.tax_percent || 0}%` : "—"}</td>
                        <td className="px-2 py-1 text-right font-bold">{on ? inr(q * r) : "—"}</td>
                      </tr>
                    );
                  })}
                  {lines.length === 0 && (
                    <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400">No eligible BOQ lines (non-excluded, quantity &gt; 0).</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[11px] text-slate-600" data-testid="boqpo-totals">
                <b>{totals.n}</b> line{totals.n === 1 ? "" : "s"} selected · Subtotal <b>{inr(totals.sub)}</b>
                {gstEnabled && <> · GST <b>{inr(totals.gst)}</b></>} · Total <b className="text-slate-900">{inr(totals.total)}</b>
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded font-bold text-slate-600">Cancel</button>
                <button data-testid="boqpo-create-btn" disabled={busy || !vendorId || !pctOk || totals.n === 0}
                  onClick={create}
                  className="px-4 py-1.5 bg-violet-700 hover:bg-violet-800 text-white font-bold rounded inline-flex items-center gap-1.5 disabled:opacity-50">
                  <FileText size={13} /> {busy ? "Creating…" : "Create Draft PO"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
